import React, { useMemo, useState, useEffect } from 'react';
import { FormHelperText, Typography } from '@mui/material';
import { partial } from 'lodash';
import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import RubricBlock from '@dyl/blocks/RubricBlock';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import TaskItems from '@dyl/blocks/TaskItems';
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';
import PlacementEditor from "@dyl/task-types/sharedLogic/SpeakingGenericEditor/PlacementEditor";
import TaskRadioImagesBlock from "@dyl/blocks/TaskRadioImagesBlock";
import SimpleTaskField from "@dyl/blocks/SimpleTaskField";

// Local helper to unify error handling
function useShowError(error, label) {
    const [show, setShow] = useState(false);
    useLocalTaskError(error && `${label}: ${error}`, () => setShow(true));
    useEffect(() => {
        if (!error) setShow(false);
    }, [error]);
    return show;
}

export default function SceneCreationEditor() {
    const [contextAudioId, setContextAudioId, contextAudioError] = useTaskField('context_audio_id');

    return (
        <TaskItems
            itemComponent={Item}
            before={(
                <AudioAndTranscriptBlock
                    label='Context Audio'
                    helperText='Audio to go with the context image.'
                    value={contextAudioId}
                    onChange={setContextAudioId}
                    error={contextAudioError}
                    required
                />
            )}
        />
    );
}

function Item({ index }) {
    const isExample = index === 0;
    const itemLabel = isExample ? 'Example' : `Item ${index}`;
    const useField = partial(useTaskField, 'items', index);

    const [audioId, setAudioId, audioError] = useField('audio_id');
    const [answers] = useField('answers');
    const [question] = useField('question');

    const radioError = useMemo(() => {
        return !answers.some(a => a.is_correct_answer)
            ? 'Must select one answer as correct.'
            : null;
    }, [answers]);

    const shouldShowRadioError = useShowError(radioError, itemLabel);

    const questionError = useMemo(() => {
        const q = typeof question === 'string' ? question.trim() : question;
        return !q ? 'Question is required.' : null;
    }, [question]);

    const shouldShowQuestionError = useShowError(questionError, itemLabel);

    return (
        <>
            <Typography variant='h6'>
                {itemLabel}
            </Typography>

            <FormHelperText error={!!shouldShowRadioError}>
                {shouldShowRadioError && radioError}
            </FormHelperText>

            {isExample && <RubricBlock />}

            <AudioAndTranscriptBlock
                id={`task-item-${index}-stimulus-audio`}
                label='Stimulus Audio'
                value={audioId}
                onChange={id => setAudioId(id)}
                error={audioError}
                required
            />

            <SimpleTaskField
                label='Question'
                path={['items', index, 'question']}
                error={!!shouldShowQuestionError}
                helperText={shouldShowQuestionError ? questionError : ' '}
                required
            />

            { isExample
                ?
                <TaskRadioImagesBlock path={['items', index, 'answers']} />
                :
                <PlacementEditor
                    index={index}
                    draggable={false}
                    hasKeySelection={true}
                    backgroundImagePath={'background_image_2'}
                />
            }
        </>
    );
}